<template>
	<div class="my-8 w-full mx-auto">
		<div>
			<div class="flex justify-between items-center">
				<div class="text-ihh-midnight-gray text-base sm:text-base font-semibold flex w-full">Shipping Address</div>
			</div>

			<!-- Selected Address Preview -->
			<div v-if="checkout.shippingAddressValidated.value && !showAddressForm" class="mb-4 mt-2 relative">
				<div
					class="w-full border mb-0 rounded h-auto p-2 text-sm text-ihh-gray-address pl-2 flex justify-between items-start"
					:class="showAddressList ? 'rounded-b-none border-b-0' : ''"
				>
					<div>
						<div class="space-x-0.5">
							<span>{{ selectedAddress.firstName }}</span>
							<span>{{ selectedAddress.lastName }}</span>
						</div>
						<!-- <div v-if="selectedAddress.phone">{{ formattedPhone }}</div> -->
						<div>{{ selectedAddress.company }}</div>
						<div>{{ selectedAddress.address1 }}</div>
						<div v-if="selectedAddress.address2">{{ selectedAddress.address2 }}</div>
						<div class="space-x-1">
							<span>{{ selectedAddress.city }},</span>
							<span>{{ selectedAddress.stateOrProvince }},</span>
							<span>{{ selectedAddress.postalCode }}</span>
							<span>/ {{ selectedAddress.countryCode || selectedAddress.country }}</span>
						</div>
					</div>
					<!-- Edit icon -->
					<div>
						<button
							@click.stop.prevent="showAddressForm = true"
							class="text-ihh-midnight-gray bg-ihh-dark-salmon hover:bg-ihh-salmon ease-in-out duration-100 transition text-xs rounded-xl px-4 py-0.5"
						>
							Edit
						</button>
					</div>
				</div>
			</div>

			<!-- Address Book Toggle (selected saved address) -->
			<div v-if="this.addresses?.length && (showAddressForm || !checkout.shippingAddressValidated.value)" class="mb-4 mt-2 relative">
				<div
					@click="toggleAddressList"
					class="w-full border mb-0 rounded h-auto p-2 text-sm text-ihh-gray-address pl-2 flex justify-between items-center cursor-pointer"
					:class="showAddressList ? 'rounded-b-none border-b-0' : ''"
				>
					<div class="flex items-center">
						<div>Select a saved address</div>
					</div>
					<!-- Dropdown arrow icon -->
					<div>
						<svg class="" height="24" viewBox="0 0 48 48" width="24" xmlns="http://www.w3.org/2000/svg">
							<path d="M14 20l10 10 10-10z" />
							<path d="M0 0h48v48h-48z" fill="none" />
						</svg>
					</div>
				</div>

				<!-- Address dropdown list -->
				<div v-if="showAddressList" class="absolute z-10 w-full bg-white border rounded rounded-t-none shadow-xl max-h-60 overflow-y-auto text-xs text-ihh-dark-gray">
					<div
						@click="selectAddress(address)"
						v-for="address in addresses"
						:key="address.id"
						class="flex flex-col justify-center p-2 hover:bg-gray-100 cursor-pointer border min-h-16 hover:cursor-pointer hover:shadow-inner"
					>
						<div class="space-x-0.5">
							<span>{{ address.firstName }}</span>
							<span>{{ address.lastName }}</span>
						</div>
						<div>{{ address.company }}</div>
						<div>{{ address.address1 }}</div>
						<div class="space-x-1">
							<span>{{ address.city }},</span>
							<span>{{ address.stateOrProvince }},</span>
							<span>{{ address.postalCode }}</span>
							<span>/ {{ address.country }}</span>
						</div>
					</div>
				</div>
			</div>

			<div v-show="showAddressForm || !checkout.shippingAddressValidated.value" class="space-y-2">
				<ShippingAddressForm
					:showAddressForm="showAddressForm"
					:addressStepCommitted="shippingAddressCommitted"
					:enableAutoComplete="enableAutoComplete"
					@newAddressCompleted="handleNewAddressCompleted"
					@shippingAddressSynced="handleShippingAddressSynced"
					@saveNewAddress="handleSaveNewAddress"
				/>
			</div>

			<template v-if="checkout.shippingAddressValidated.value && !showAddressForm">
				<!-- Set Default Address -->
				<div v-if="store?.user?.id && !checkout.shipping.shippingAddress.isDefault" class="m-auto mt-6 items-center flex w-full">
					<div class="flex items-center m-auto w-full pr-4">
						<label class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2">
							<input type="checkbox" v-model="setDefaultAddress" class="absolute opacity-0 w-0 h-0" />
							<span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg">✓</span>
						</label>
						<div class="text-ihh-slate-gray text-sm items-center text-start">Set address as default</div>
					</div>
				</div>

				<ShippingMethods
					:methods="checkout.shipping.shippingOptions"
					:selectedMethod="checkout.shipping.selectedShipping"
					:loadingShipping="loadingShipping"
					@shipping-method-selected="handleShippingMethodSelected"
				/>

				<div class="text-ihh-midnight-gray text-base sm:text-base font-semibold my-3 flex w-full">Order Comments</div>
				<input
					v-model="orderComments"
					type="text"
					placeholder="Type here..."
					required
					class="focus:border-ihh-dark-salmon focus:outline-0 focus:shadow shadow-ihh-dark-salmon text-ihh-midnight-gray placeholder-ihh-slate-gray border border-ihh-gray/60 rounded px-3 py-3 flex w-full text-sm bg-gradient-to-r from-ihh-pink/20 to-ihh-light-lavendar/20"
				/>

				<div class="w-full flex justify-center md:justify-start mt-4 md:mt-0">
					<button
						@click="handleContinue"
						class="flex w-full justify-center md:max-w-40 text-cendter mt-4 uppercase bg-ihh-dark-salmon hover:bg-ihh-salmon ease-in-out duration-100 transition text-ihh-midnight-gray font-semibold tracking-wide py-3 px-4 text-sm rounded items-center"
					>
						Continue
					</button>
				</div>
			</template>
		</div>
		<!-- Shipping Address Confirmation Modal -->
		<ShippingAddressConfirmation v-model="showShippingAddressConfirmationModal" @address-confirmed="handleAddressConfirmed" @edit-address="handleEditAddress" />
	</div>
</template>

<script>
import { useCheckoutStore } from '../../stores/store.js'
import helpers from '../../helpers/checkout.js'

// Components
import ShippingAddressForm from '../checkout/shipping-address-form.vue'
import ShippingMethods from '../checkout/shipping-methods.vue'
import ShippingAddressConfirmation from '../layout/shipping-address-confirmation.vue'

// Libraries
import Swal from 'sweetalert2'
import axios from 'axios'
import { storedAuthorization } from '../../helpers/session.js'

export default {
	name: 'ShippingAddressDetails',
	inject: ['store'],
	components: { ShippingAddressForm, ShippingMethods, ShippingAddressConfirmation },
	emits: ['saveNewAddress', 'continue', 'addressSelected', 'shippingAddressSynced', 'field-validation'],
	props: {
		addresses: {
			type: Array,
			default: () => [],
		},
		selectedAddress: {
			type: Object,
			default: null,
		},
		enableAutoComplete: {
			type: Boolean,
			default: false,
		},
		loadingShipping: {
			type: Boolean,
			default: false,
		},
		hideShippingAddressForm: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			// UI Variables
			showAddressList: false,
			showAddressForm: false,
			showShippingAddressConfirmationModal: false,

			// Data
			saving: false,
			orderComments: '',
			fieldValidationStatus: [],
			setDefaultAddress: false,
			checkout: useCheckoutStore(),
			// True after the user has clicked Continue on the address form at least once
			shippingAddressCommitted: false,
		}
	},
	mounted() {
		// Show address form if no addresses are available
		if (!this.addresses?.length && !this.selectedAddress?.city) this.showAddressForm = true

		if (this.checkout.orderDetails.comments.length) this.orderComments = this.checkout.orderDetails.comments

		// Returning to checkout with a saved address already past the form step
		if (this.checkout.shippingAddressValidated.value && !this.showAddressForm) {
			this.shippingAddressCommitted = true
		}
	},
	methods: {
		toggleAddressList() {
			this.showAddressList = !this.showAddressList
		},
		selectAddress(address) {
			this.updateSelectedAddress(address)

			this.showAddressList = false
			this.showAddressForm = false

			this.$emit('addressSelected')
		},
		toggleAddressForm() {
			this.checkout.resetShippingAddress()
			this.showAddressForm = true
			this.showAddressList = false
		},
		updateSelectedAddress(address) {
			const result = this.checkout.commitShippingAddress(JSON.parse(JSON.stringify(address)))
			if (result.needsShippingRefetch) {
				this.$emit('shippingAddressSynced', {
					addressChanged: result.addressChanged,
					previousMethodId: result.previousMethodId,
					needsShippingRefetch: true,
				})
			}
		},
		async handleContinue() {
			if (!this.checkout.shippingMethodSelected) {
				if (this.checkout.shipping?.quoteNetworkError) {
					await helpers.getShippingEstimates()
					return
				}
				Swal.fire({
					icon: 'warning',
					text: 'Please select a shipping method',
					confirmButtonText: 'OK',
					confirmButtonColor: '#e59789',
				})
				return
			}

			// If the user dismissed the confirmation modal (X icon) earlier, re-show it now.
			// They must explicitly Confirm Address (or Edit Address) before proceeding.
			if (!this.checkout.isShippingValidated) {
				this.showShippingAddressConfirmationModal = true
				return
			}

			if (this.setDefaultAddress) {
				const updateAddressResponse = axios
					.post(`${this.store.api}/controllers/bigCommerce/addresses`, this.checkout.shipping.shippingAddress, {
						headers: {
							Authorization: storedAuthorization(),
							Accept: 'application/json',
						},
					})
					.then((response) => {
						if (response?.data?.success) {
							this.checkout.shipping.shippingAddress.isDefault = true
						}
					})
			}
			this.checkout.setOrderComments(this.orderComments)
			this.showAddressList = false
			this.$emit('continue')
		},
		handleNewAddressCompleted(syncMeta) {
			this.shippingAddressCommitted = true
			this.$emit('addressSelected', {
				previousMethodId: syncMeta?.previousMethodId || null,
			})
			this.showAddressForm = false
		},
		handleShippingAddressSynced(syncMeta) {
			this.$emit('shippingAddressSynced', syncMeta)
		},
		handleSaveNewAddress(value) {
			this.$emit('saveNewAddress', value)
		},
		handleAddressConfirmed() {
			this.showShippingAddressConfirmationModal = false
			this.checkout.updateShippingValidation(true)
			// Proceed to billing automatically — the user already clicked Continue once
			// (which opened this modal) and has now explicitly confirmed, so no need to make
			// them click Continue a second time.
			this.handleContinue()
		},
		handleEditAddress() {
			this.showShippingAddressConfirmationModal = false
			this.showAddressForm = true
		},
		handleShippingMethodSelected() {
			if (!this.checkout.isShippingValidated) {
				this.showShippingAddressConfirmationModal = true
			}
		},
	},

	computed: {
		formattedPhone() {
			const phone = this.selectedAddress?.phone || ''
			const digits = phone.replace(/\D/g, '') // Remove non-numeric characters
			return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') // Format as 512-555-5555
		},
	},

	watch: {
		hideShippingAddressForm: {
			handler(newVal) {
				if (newVal && this.checkout.shippingAddressValidated.value && this.checkout.shipping.shippingOptions?.length) {
					this.showAddressForm = false
					this.$emit('update:hideShippingAddressForm', false)
				}
			},
		},
	},
}
</script>

<style scoped>
.custom-checkbox {
	appearance: none;
	-webkit-appearance: none;
	-moz-appearance: none;
	width: 16px; /* Matches Tailwind h-4 w-4 */
	height: 16px;
	border: 2px solid #d1d5db; /* Tailwind border-gray-300 */
	border-radius: 0.25rem; /* Tailwind rounded */
	background-color: white;
	cursor: pointer;
}

.custom-checkbox:checked {
	background-color: #f28c82; /* Replace with your bg-ihh-dark-salmon hex code */
	border-color: #f28c82;
	background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.207 5.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3E%3C/svg%3E");
}

.custom-checkbox:focus {
	outline: none;
	box-shadow: 0 0 0 2px #f28c82; /* Matches Tailwind focus:ring-ihh-dark-salmon */
}
</style>
